import { useQuery } from '@apollo/client/react'
import { Button, Center, Spinner, Text, VStack } from '@chakra-ui/react'
import { useChatContext } from '@src/components/sco/Chat/ChatContext'
import { ChatThreadListItem } from '@src/components/sco/Chat/ChatThreadListItem'
import { ScoThread } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import { useEffect, useMemo, useState } from 'react'

const PAGE_SIZE = 50

export const ChatDirectThreadList = (props: {
  onThreadSelect?: (thread: ScoThread) => void
  attachmentType?: string
  attachmentId?: string
  blankState?: any
  showEmptyThreads?: boolean
  /** Search string used to filter threads by member name (server-side via memberNames). */
  search?: string
}) => {
  const chatContext = useChatContext()
  const { currentUser } = useCurrentUser()
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const memberNames = useMemo(() => {
    const trimmed = props.search?.trim()
    return trimmed ? [trimmed] : undefined
  }, [props.search])

  // Reset pagination when the search filter changes.
  useEffect(() => {
    setPage(1)
  }, [memberNames])

  const query = useQuery(
    gql`
    query ChatInboxDirectThreads(
      $attachmentType: String
      $attachmentId: String
      $memberNames: [String]
      $page: Int
      $pageSize: Int
    ) {
      chat {
        id
        inboxThreads(
          threadTypeKey: "direct-chat"
          attachmentType: $attachmentType
          attachmentId: $attachmentId
          memberNames: $memberNames
          page: $page
          pageSize: $pageSize
        ) {
          id
          items {
            id
            ${ChatThreadListItem.fragments.ScoInboxThread()}
          }
          pagination {
            hasNextPage
            page
            pageSize
            itemCount
          }
        }
      }
    }
  `,
    {
      ssr: false,
      variables: {
        attachmentType: props.attachmentType,
        attachmentId: props.attachmentId,
        memberNames,
        page: 1,
        pageSize: PAGE_SIZE,
      },
    }
  )

  const includeMemberInbox = !props.attachmentType && !props.attachmentId && !!currentUser?.userId
  const memberInboxQuery = useQuery(
    gql`
    query ChatInboxMemberToolThreads($memberUserId: String, $memberNames: [String], $pageSize: Int) {
      chat {
        id
        inboxThreads(
          threadTypeKey: "tool-chat"
          toolSessionAttachmentType: "MemberInbox"
          toolSessionAttachmentId: $memberUserId
          memberNames: $memberNames
          page: 1
          pageSize: $pageSize
        ) {
          id
          items {
            id
            ${ChatThreadListItem.fragments.ScoInboxThread()}
          }
        }
      }
    }
  `,
    {
      ssr: false,
      skip: !includeMemberInbox,
      variables: {
        memberUserId: currentUser?.userId,
        memberNames,
        pageSize: PAGE_SIZE,
      },
    }
  )

  const directItems = query.data?.chat?.inboxThreads?.items
  const memberInboxItems = memberInboxQuery.data?.chat?.inboxThreads?.items
  const threads = useMemo(() => {
    const seen = new Set<string>()
    return [...(directItems || []), ...(memberInboxItems || [])]
      .filter(thread => {
        if (!thread?.threadId || seen.has(thread.threadId)) {
          return false
        }
        seen.add(thread.threadId)
        return true
      })
      .sort((a, b) => {
        const aTime = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0
        const bTime = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0
        return bTime - aTime
      })
  }, [directItems, memberInboxItems])
  const pagination = query.data?.chat?.inboxThreads?.pagination
  const hasNextPage = Boolean(pagination?.hasNextPage)

  useEffect(() => {
    if (refetchTrigger) {
      setPage(1)
      query.refetch({
        attachmentType: props.attachmentType,
        attachmentId: props.attachmentId,
        memberNames,
        page: 1,
        pageSize: PAGE_SIZE,
      })
      if (includeMemberInbox) {
        memberInboxQuery.refetch({
          memberUserId: currentUser?.userId,
          memberNames,
          pageSize: PAGE_SIZE,
        })
      }
    }
  }, [refetchTrigger])

  useEffect(() => {
    const activeThreadId = chatContext.activeThreadId
    if (typeof activeThreadId !== 'string' || !activeThreadId) {
      return
    }
    if (!query.data) {
      return
    }
    if (!threads.find(thread => thread.threadId === activeThreadId)) {
      query.refetch()
      if (includeMemberInbox) {
        memberInboxQuery.refetch()
      }
    }
  }, [threads.map((t: ScoThread) => t.threadId).join(';'), chatContext.activeThreadId])

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasNextPage) {
      return
    }

    const nextPage = page + 1
    setIsLoadingMore(true)
    try {
      await query.fetchMore({
        variables: {
          attachmentType: props.attachmentType,
          attachmentId: props.attachmentId,
          memberNames,
          page: nextPage,
          pageSize: PAGE_SIZE,
        },
      })
      setPage(nextPage)
    } catch (err) {
      console.error('Error loading more threads:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }

  if (
    (query.loading && !query.data) ||
    (includeMemberInbox && memberInboxQuery.loading && !memberInboxQuery.data && threads.length === 0)
  ) {
    return (
      <Center padding={4}>
        <Spinner />
      </Center>
    )
  }

  if (!threads || threads.length === 0) {
    if (memberNames) {
      return (
        <Center padding={4}>
          <Text color={'colorPalette.fg/60'} fontSize={'sm'} textAlign={'center'}>
            No conversations match your search.
          </Text>
        </Center>
      )
    }
    return props.blankState ? props.blankState : <></>
  }

  return (
    <VStack align={'stretch'} gap={0} width={'full'}>
      {threads.map(thread => (
        <ChatThreadListItem
          key={thread.threadId}
          isActive={thread.threadId === chatContext.activeThreadId}
          showEmptyThreads={props.showEmptyThreads}
          thread={thread}
          triggerRefetch={() => setRefetchTrigger(refetchTrigger + 1)}
          onSelect={
            props.onThreadSelect ||
            (selectedThread => chatContext?.setActiveThreadId(selectedThread.threadId))
          }
        />
      ))}
      {hasNextPage && (
        <Center padding={3}>
          <Button
            loading={isLoadingMore}
            size={'sm'}
            variant={'ghost'}
            width={'full'}
            onClick={handleLoadMore}
          >
            Load More
          </Button>
        </Center>
      )}
    </VStack>
  )
}
